"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useToast } from "@/context/ToastContext";
import { Store, Loader2, Check, ArrowRight } from "lucide-react";
import { Link } from "@/i18n/routing";

export default function BecomeAResellerPage() {
  const { data: session, status } = useSession();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    businessName: "",
    studioAddress: "",
    studioCity: "",
    studioState: "",
    studioZip: "",
    phone: "",
    socialMedia: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.businessName.trim() || !form.phone.trim()) {
      toast("Business name and phone are required", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/reseller/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to submit");
      setSubmitted(true);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to submit", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const isLoggedIn = status === "authenticated" && session?.user;
  const isLoading = status === "loading";

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-background to-brand-cream/30">
        <div className="container-luxury py-16 sm:py-24 max-w-3xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-brand-gold/10 mb-6">
              <Store className="h-8 w-8 text-brand-gold" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-brand-dark mb-4">
              Become a Reseller
            </h1>
            <p className="text-brand-muted max-w-lg mx-auto leading-relaxed">
              Join the Rivky Wigs reseller network. Access wholesale pricing, browse our full catalog,
              place orders for your clients, and submit wig repairs — all from your dedicated portal.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[
              { title: "Wholesale Pricing", desc: "Access exclusive reseller pricing on our entire collection" },
              { title: "Client Portal", desc: "Browse and present wigs to your clients from a dedicated interface" },
              { title: "Wig Repairs", desc: "Submit repair requests with pickup arranged from your studio" },
            ].map((b) => (
              <div key={b.title} className="bg-white/80 rounded-xl p-5 border border-brand-dark/5">
                <h3 className="font-display text-sm text-brand-dark mb-1">{b.title}</h3>
                <p className="text-xs text-brand-muted leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Registration fee note */}
          <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl p-5 mb-8 text-center">
            <p className="text-sm text-brand-dark">
              <strong>$2,000 one-time registration fee</strong> to open your reseller account.
              Payment is collected after your application is approved.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-6">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="font-display text-2xl text-brand-dark mb-3">Application Submitted!</h2>
              <p className="text-brand-muted mb-8 max-w-md mx-auto">
                We&apos;ll review your application and get back to you within 1-2 business days.
                You&apos;ll receive a notification when your account is ready.
              </p>
              <Link
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-dark text-white rounded-lg hover:bg-brand-gold transition-colors text-sm font-medium"
              >
                Back to Home
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-brand-gold" />
            </div>
          ) : !isLoggedIn ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-brand-dark/5 shadow-sm">
              <h3 className="font-display text-xl text-brand-dark mb-3">Create an Account First</h3>
              <p className="text-sm text-brand-muted mb-6 max-w-sm mx-auto">
                You need an account to apply as a reseller. Register or log in to continue.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  href="/login?callbackUrl=/become-a-reseller"
                  className="px-6 py-2.5 bg-brand-dark text-white rounded-lg text-sm font-medium hover:bg-brand-gold transition-colors"
                >
                  Log In
                </Link>
                <Link
                  href="/login?callbackUrl=/become-a-reseller"
                  className="px-6 py-2.5 border border-brand-dark/15 text-brand-dark rounded-lg text-sm font-medium hover:bg-brand-cream transition-colors"
                >
                  Register
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-brand-dark/5 shadow-sm p-6 sm:p-8 space-y-6">
              <h3 className="font-display text-xl text-brand-dark">Your Application</h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Business Name *</label>
                  <input
                    type="text"
                    value={form.businessName}
                    onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                    placeholder="Your wig studio name"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Phone *</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Studio Address</label>
                <input
                  type="text"
                  value={form.studioAddress}
                  onChange={(e) => setForm({ ...form, studioAddress: e.target.value })}
                  placeholder="Street address"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">City</label>
                  <input
                    type="text"
                    value={form.studioCity}
                    onChange={(e) => setForm({ ...form, studioCity: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">State</label>
                  <input
                    type="text"
                    value={form.studioState}
                    onChange={(e) => setForm({ ...form, studioState: e.target.value.toUpperCase() })}
                    maxLength={2}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Instagram / Website</label>
                <input
                  type="text"
                  value={form.socialMedia}
                  onChange={(e) => setForm({ ...form, socialMedia: e.target.value })}
                  placeholder="@yourstudio or https://..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Tell us about yourself</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How many clients do you serve? What area do you cover? Why are you interested in becoming a reseller?"
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/40 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 py-3 bg-brand-dark text-white rounded-lg text-sm font-medium hover:bg-brand-gold transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <><Store className="h-4 w-4" /> Submit Application</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
